/**
 * F44 (Wave 2D) — admin finance settlements page.
 *
 * Route: `/finance/settlements`. Permission: `financial:read`.
 *
 * A sortable/paginated Table over GET /admin/financial/settlements with
 * source / status / aging / payer-entity / date-range / "needs reconciliation"
 * filters. Each row deep-links to F23 mark-settled (`/commission-statements/:id`
 * via the row's `deepLinkPath`). Read-only here — F44 writes no financial data.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  Button,
  CurrencyDisplay,
  DateRangePicker,
  EmptyState,
  Pagination,
  Select,
  Skeleton,
  Switch,
  Table,
  TextInput,
  type TableColumn,
} from '@bravemove/ui-web';
import {
  AR_AGING_BUCKETS,
  FINANCE_REVENUE_SOURCES,
  FINANCE_STATEMENT_STATUSES,
  type ArAgingBucket,
  type FinanceRevenueSource,
  type FinanceSettlementRow,
  type FinanceStatementStatus,
} from '@bravemove/types';
import { adminFinancialApi, type SettlementsOpts } from '../../api/financial';
import { PageHeader } from '../../components/PageHeader';
import {
  defaultFinanceRange,
  formatPeriodMonth,
  formatShortDate,
} from '../../components/finance/financeHelpers';
import { FinanceNav } from './FinanceNav';

const PAGE_SIZE = 25;

export function FinanceSettlementsPage() {
  const { t, locale } = useI18n();
  const [searchParams] = useSearchParams();

  const defaultRange = useMemo(() => defaultFinanceRange(), []);
  const [from, setFrom] = useState(defaultRange.from);
  const [to, setTo] = useState(defaultRange.to);
  const [source, setSource] = useState<FinanceRevenueSource | ''>('');
  const [status, setStatus] = useState<FinanceStatementStatus | ''>('');
  const [aging, setAging] = useState<ArAgingBucket | ''>('');
  // Seed the payer filter from the F42 venue-detail deep-link
  // (`/finance/settlements?entityId=<venueId>`).
  const [entityId, setEntityId] = useState(() => searchParams.get('entityId') ?? '');
  const [needsReconciliation, setNeedsReconciliation] = useState(false);
  const [page, setPage] = useState(1);

  const [rows, setRows] = useState<readonly FinanceSettlementRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const opts: SettlementsOpts = {
        from,
        to,
        page,
        perPage: PAGE_SIZE,
        ...(source ? { source } : {}),
        ...(status ? { status } : {}),
        ...(aging ? { aging } : {}),
        ...(entityId ? { entityId } : {}),
        ...(needsReconciliation ? { needsReconciliation: true } : {}),
      };
      const data = await adminFinancialApi.listSettlements(opts);
      setRows(data.data);
      setTotal(data.pagination.total);
    } catch {
      setError(t('admin:financial.errors.generic'));
    } finally {
      setLoading(false);
    }
  }, [from, to, source, status, aging, entityId, needsReconciliation, page, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchList();
  }, [fetchList]);

  const onClear = () => {
    setSource('');
    setStatus('');
    setAging('');
    setEntityId('');
    setNeedsReconciliation(false);
    setPage(1);
  };

  const hasFilters =
    source !== '' || status !== '' || aging !== '' || entityId !== '' || needsReconciliation;

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const columns = useMemo<readonly TableColumn<FinanceSettlementRow>[]>(
    () => [
      {
        key: 'entity',
        header: t('admin:financial.settlements.column.entity'),
        cell: (row) => (
          <div className="flex flex-col gap-0.5">
            <span className="text-[13px] font-semibold text-ink">{row.entityName}</span>
            <span className="font-mono text-[11px] text-ink-3">{row.entityKind}</span>
          </div>
        ),
      },
      {
        key: 'source',
        header: t('admin:financial.settlements.column.source'),
        cell: (row) => (
          <span className="font-mono text-[11px] uppercase tracking-wide text-ink-2">
            {t(`commission:source.${row.source}`, { defaultValue: row.source })}
          </span>
        ),
      },
      {
        key: 'period',
        header: t('admin:financial.settlements.column.period'),
        cell: (row) => (
          <span className="text-[12.5px] text-ink">
            {formatPeriodMonth(row.periodStart, locale)}
          </span>
        ),
      },
      {
        key: 'status',
        header: t('admin:financial.settlements.column.status'),
        sortable: true,
        sortAccessor: (row) => row.status,
        cell: (row) => (
          <span className="font-mono text-[11px] uppercase tracking-wide text-ink-2">
            {t(`commission:status.${row.status}`, { defaultValue: row.status })}
          </span>
        ),
      },
      {
        key: 'aging',
        header: t('admin:financial.settlements.column.aging'),
        cell: (row) =>
          row.agingBucket ? (
            <span className="text-[12px] text-ink-2">
              {t(`admin:financial.aging.${row.agingBucket}`)}
            </span>
          ) : (
            <span className="text-ink-3">—</span>
          ),
      },
      {
        key: 'amount',
        header: t('admin:financial.settlements.column.amount'),
        align: 'end',
        sortable: true,
        sortAccessor: (row) => row.amountMinor,
        cell: (row) => (
          <CurrencyDisplay amount={row.amountMinor} currency={row.currency} locale={locale} />
        ),
      },
      {
        key: 'due',
        header: t('admin:financial.settlements.column.due'),
        cell: (row) => (
          <span className="text-[12px] text-ink-2">{formatShortDate(row.dueAt, locale)}</span>
        ),
      },
      {
        key: 'actions',
        header: t('admin:financial.settlements.column.actions'),
        align: 'end',
        cell: (row) => (
          <Link
            to={row.deepLinkPath}
            className="text-[12px] font-semibold text-accent hover:underline"
            data-testid={`finance-settlement-open-${row.statementId}`}
          >
            {t('admin:financial.settlements.open')}
          </Link>
        ),
      },
    ],
    [t, locale],
  );

  return (
    <div className="space-y-6" data-testid="finance-settlements-page">
      <PageHeader
        eyebrow="Finance"
        title={t('admin:financial.settlements.title')}
        subtitle={t('admin:financial.settlements.subtitle')}
      />
      <FinanceNav />

      <div className="flex flex-wrap items-end gap-3" data-testid="finance-settlements-filters">
        <FilterField label={t('admin:financial.settlements.filters.source')}>
          <Select
            value={source}
            placeholder={t('admin:financial.settlements.filters.all')}
            options={FINANCE_REVENUE_SOURCES.map((s) => ({
              value: s,
              label: t(`commission:source.${s}`, { defaultValue: s }),
            }))}
            onChange={(e) => {
              setSource(e.target.value as FinanceRevenueSource | '');
              setPage(1);
            }}
            data-testid="finance-settlements-source"
            aria-label={t('admin:financial.settlements.filters.source')}
          />
        </FilterField>
        <FilterField label={t('admin:financial.settlements.filters.status')}>
          <Select
            value={status}
            placeholder={t('admin:financial.settlements.filters.all')}
            options={FINANCE_STATEMENT_STATUSES.map((s) => ({
              value: s,
              label: t(`commission:status.${s}`, { defaultValue: s }),
            }))}
            onChange={(e) => {
              setStatus(e.target.value as FinanceStatementStatus | '');
              setPage(1);
            }}
            data-testid="finance-settlements-status"
            aria-label={t('admin:financial.settlements.filters.status')}
          />
        </FilterField>
        <FilterField label={t('admin:financial.settlements.filters.aging')}>
          <Select
            value={aging}
            placeholder={t('admin:financial.settlements.filters.all')}
            options={AR_AGING_BUCKETS.map((b) => ({
              value: b,
              label: t(`admin:financial.aging.${b}`),
            }))}
            onChange={(e) => {
              setAging(e.target.value as ArAgingBucket | '');
              setPage(1);
            }}
            data-testid="finance-settlements-aging"
            aria-label={t('admin:financial.settlements.filters.aging')}
          />
        </FilterField>
        <FilterField label={t('admin:financial.settlements.filters.entityId')}>
          <TextInput
            value={entityId}
            onChange={(e) => {
              setEntityId(e.target.value);
              setPage(1);
            }}
            placeholder={t('admin:financial.settlements.filters.entityIdPlaceholder')}
            data-testid="finance-settlements-entity"
          />
        </FilterField>
        <div className="pb-1">
          <Switch
            label={t('admin:financial.settlements.filters.needsReconciliation')}
            checked={needsReconciliation}
            onChange={(e) => {
              setNeedsReconciliation(e.target.checked);
              setPage(1);
            }}
            data-testid="finance-settlements-needs-reconciliation"
          />
        </div>
        {hasFilters ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClear}
            data-testid="finance-settlements-clear"
          >
            {t('admin:financial.settlements.filters.clear')}
          </Button>
        ) : null}
      </div>

      <DateRangePicker
        value={{ start: from, end: to }}
        onChange={(dr) => {
          setFrom(dr.start ?? from);
          setTo(dr.end ?? to);
          setPage(1);
        }}
        locale={locale}
      />

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading ? (
        <div className="space-y-3" data-testid="finance-settlements-loading">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-lg" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState title={t('admin:financial.settlements.empty')} />
      ) : (
        <>
          <Table
            data={rows as FinanceSettlementRow[]}
            columns={columns as TableColumn<FinanceSettlementRow>[]}
            rowKey={(row) => row.statementId}
            ariaLabel={t('admin:financial.settlements.title')}
          />
          {pageCount > 1 ? (
            <div className="flex justify-end">
              <Pagination
                page={page}
                pageCount={pageCount}
                onChange={setPage}
                ariaLabel={t('admin:financial.settlements.title')}
              />
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

function FilterField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-[160px] max-w-xs">
      <label className="mb-1 block text-[11px] uppercase tracking-wider text-ink-3">{label}</label>
      {children}
    </div>
  );
}

export default FinanceSettlementsPage;
